import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import toast from 'react-hot-toast';
import { postRequest } from '../../../lib/api';
import { AuthContext } from '../../../authContext/context';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';

export default function EditLssWillLead() {
  const { id } = useParams();
  const navigate = useNavigate();
  const authCtx = React.useContext(AuthContext);
  const [loading, setLoading] = React.useState(false);
  const [overrideDate, setOverrideDate] = React.useState(dayjs());
  const [groupCodes, setGroupCodes] = React.useState([]);
  const [partners, setPartners] = React.useState([]);
  const [lenderPanels, setLenderPanels] = React.useState([]);
  const [isPostcodeValid, setIsPostcodeValid] = React.useState(false);
  const [postcodeError, setPostcodeError] = React.useState('');
  const [postcodeMatch, setPostcodeMatch] = React.useState(null);
  const [checkingPostcode, setCheckingPostcode] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch
  } = useForm({
    defaultValues: {
      overrideDate: dayjs(),
      group_code: '',
      partner_id: '',
      lender_panel_id: '',
      acquisition: 'Inbound',
      address1: '',
      address2: '',
      town: '',
      country: '',
      postcode: '',
      typeOfService: 'Single',
      about: [],
      approximateValueOfAssets: 'Upto £500,000'
    }
  });

  const aboutOptions = [
    { label: "Do you have minor beneficiaries?" },
    { label: "Do you have a life interest trust?" },
    { label: "Do you have any Foreign Assets?" },
    { label: "Do you have more than one property?" },
    { label: "Will there be more than 5 beneficiaries?" }
  ];

  React.useEffect(() => {
    if (authCtx.token && id) {
      fetchGroupCodes();
      fetchPartners();
      fetchLenderPanels();
      fetchLeadData();
    }
  }, [id, authCtx.token]);

  const fetchGroupCodes = async () => {
    try {
      const res = await postRequest('private/getGroupCodeList', {}, authCtx.token);
      if (res?.status === 200 && Array.isArray(res.data)) {
        setGroupCodes(res.data);
        console.log('📋 Group Codes fetched:', res.data);
      }
    } catch (err) {
      console.error('Error fetching group codes:', err);
    }
  };

  const fetchPartners = async () => {
    try {
      const res = await postRequest('private/partnerList', { page: 1, limit: 100 }, authCtx.token);
      if (res?.status === 200 && res?.data?.docs) {
        setPartners(res.data.docs);
        console.log('📋 Partners fetched:', res.data.docs);
      }
    } catch (err) {
      console.error('Error fetching partners:', err);
    }
  };

  const fetchLenderPanels = async () => {
    try {
      const res = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      if (res?.status === 200 && res?.data) {
        setLenderPanels(res.data);
        console.log('📋 Lender Panels fetched:', res.data);
      }
    } catch (err) {
      console.error('Error fetching lender panels:', err);
    }
  };

  const fetchLeadData = async () => {
    setLoading(true);
    try {
      const response = await postRequest(`private/get-single-lead/${id}`, {}, authCtx.token);
      const lead = response?.data;

      if (lead) {
        console.log('📋 Edit LSS Will - Lead Data Fetched:', lead);
        console.log('📝 Lead About:', lead.about);
        console.log('📝 About type:', typeof lead.about);
        console.log('📝 Is Array?:', Array.isArray(lead.about));

        // Convert about to array format
        let aboutArray = [];

        // Check if about is already an array (new format)
        if (Array.isArray(lead.about)) {
          aboutArray = lead.about;
          console.log('✅ Using array format');
        }
        // Check if about exists as individual boolean fields (old format) - convert them
        else if (lead.minorBeneficiaries !== undefined || lead.lifeInterestTrust !== undefined) {
          console.log('🔄 Converting boolean format to array');
          if (lead.minorBeneficiaries) aboutArray.push(aboutOptions[0].label);
          if (lead.lifeInterestTrust) aboutArray.push(aboutOptions[1].label);
          if (lead.foreignAssets) aboutArray.push(aboutOptions[2].label);
          if (lead.moreThanOneProperty) aboutArray.push(aboutOptions[3].label);
          if (lead.moreThan5Beneficiaries) aboutArray.push(aboutOptions[4].label);
        } else {
          console.log('⚠️ About field is neither array nor boolean fields:', lead.about);
        }

        console.log('✅ Final About Array:', aboutArray);
        console.log('✅ About Array length:', aboutArray.length);

        console.log('📍 Address Data from API:', lead.address);

        // Normalize service_type to match dropdown values (capitalize first letter)
        let serviceType = lead.service_type || 'Single';
        if (serviceType && typeof serviceType === 'string') {
          serviceType = serviceType.charAt(0).toUpperCase() + serviceType.slice(1).toLowerCase();
        }

        const formValues = {
          overrideDate: lead.override_date ? dayjs(lead.override_date) : dayjs(),
          group_code: lead.group_code?._id ? String(lead.group_code._id) : '',
          partner_id: lead.partner_id?._id ? String(lead.partner_id._id) : '',
          lender_panel_id: lead.lender_panel_id?._id ? String(lead.lender_panel_id._id) : '',
          acquisition: lead.acquisition || 'Inbound',
          address1: lead.address?.address1 || '',
          address2: lead.address?.address2 || '',
          town: lead.address?.town || '',
          country: lead.address?.country || '',
          postcode: lead.address?.postcode || '',
          typeOfService: serviceType,
          about: aboutArray,
          approximateValueOfAssets: lead.approximate_value_of_assets || 'Upto £500,000'
        };

        console.log('🎯 Resetting form with values:', formValues);
        console.log('🎯 About field value being set:', formValues.about);
        console.log('🎯 Address fields being set:', {
          address1: formValues.address1,
          address2: formValues.address2,
          town: formValues.town,
          country: formValues.country,
          postcode: formValues.postcode
        });
        console.log('🎯 Type of Service being set:', formValues.typeOfService);
        console.log('🎯 Group Code being set:', formValues.group_code);

        reset(formValues);

        if (lead.override_date) {
          setOverrideDate(dayjs(lead.override_date));
        }

        console.log('✅ Form reset complete');
      }
    } catch (error) {
      console.error('Error fetching lead data:', error);
      toast.error('Failed to fetch lead data');
    } finally {
      setLoading(false);
    }
  };

  // Validate postcode and match with branch
  const validatePostcode = async (postcodeValue) => {
    console.log('🔍 Validating postcode:', postcodeValue);
    setCheckingPostcode(true);

    try {
      // Reset states
      setPostcodeMatch(null);
      setIsPostcodeValid(false);
      setPostcodeError('');

      // STEP 1: Validate postcode exists in UK database
      console.log('📤 Step 1: Validating postcode with UK database');
      const response = await postRequest('private/validatePostcode', { postcode: postcodeValue }, authCtx.token);
      console.log('📡 Step 1 Response:', response);

      // Check if postcode is INVALID
      if (response.success !== true || !response.data) {
        console.log('❌ Step 1 Failed - Invalid postcode');
        setIsPostcodeValid(false);
        setPostcodeError('Invalid UK postcode');
        toast.error('Please enter a valid UK postcode. This postcode does not exist in our database.');
        setCheckingPostcode(false);
        return false;
      }

      // STEP 2: Match postcode with branch
      console.log('✅ Step 1 Passed - Valid postcode');
      console.log('🔍 Step 2: Matching postcode with branch');
      const matchRes = await postRequest(
        'private/matchBranchByPostcode',
        { postcode: postcodeValue.trim() },
        authCtx.token
      );
      console.log('📡 Step 2 Response:', matchRes);

      if (matchRes?.data?.matched) {
        // ✅ Branch matched
        console.log('✅ Step 2 Passed - Branch matched:', matchRes.data.branch);
        setPostcodeMatch(matchRes.data.branch);
        setIsPostcodeValid(true);
        setPostcodeError('');
        setCheckingPostcode(false);
        return true;
      } else {
        // ❌ No branch matched
        console.log('❌ Step 2 Failed - No branch matched');
        setIsPostcodeValid(false);
        setPostcodeError('No branch available for this postcode');
        toast.error(matchRes?.message || 'No branch is available for this postcode area.');
        setCheckingPostcode(false);
        return false;
      }

    } catch (error) {
      console.error('❌ Postcode validation error:', error);
      setIsPostcodeValid(false);
      setPostcodeError('Could not validate postcode');
      toast.error('Could not validate postcode. Please try again.');
      setCheckingPostcode(false);
      return false;
    }
  };

  const onSubmit = async (data) => {
    // Validate postcode before submission
    if (data.postcode) {
      const postcodeValue = data.postcode.trim();
      const isValid = await validatePostcode(postcodeValue);
      if (isValid === false) {
        console.log('❌ Form submission blocked - Invalid postcode');
        return; // Stop form submission
      }
    }
    const created_id = localStorage.getItem('id');

    // Calculate complexity based on approximate value of assets
    let complexity = 'Low';
    if (data.approximateValueOfAssets === 'Over £1,000,000') {
      complexity = 'Medium';
    }

    const payload = {
      override_date: overrideDate.toISOString(),
      group_code: data.group_code,
      partner_id: data.partner_id || null,
      lender_panel_id: data.lender_panel_id || null,
      acquisition: data.acquisition,
      address: {
        address1: data.address1,
        address2: data.address2,
        town: data.town,
        country: data.country,
        postcode: data.postcode,
      },
      service_type: data.typeOfService,
      about: data.about,
      approximate_value_of_assets: data.approximateValueOfAssets,
      complexity: complexity,
      created_id: created_id,
      branch_id: postcodeMatch?._id || null // Add matched branch ID
    };

    console.log('📤 LSS Will Edit Payload:', payload);
    console.log('📌 Matched Branch:', postcodeMatch);

    setLoading(true);
    try {
      const response = await postRequest(`private/lead-edit/${id}`, payload, authCtx.token);
      if (response?.status === 'success' || response?.status === 200) {
        toast.success('LSS Will lead updated successfully!');
        navigate(`/leads/view/lss-will/${id}`);
      } else {
        toast.error(response?.message || 'Update failed');
      }
    } catch (error) {
      toast.error(error?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: `Edit LSS Will #${id}`, path: '' },
        ]}
      />
      <br />

      <div className="page-header pb-3">
        <h1 className="mt-0 mb-0">Edit LSS Will #{id}</h1>
      </div>

      <div className="page-top-formview mb-3 edit-lead">
        <div style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '24px'
        }}>
          <h5 className="mb-4">Edit LSS Will #{id}</h5>
          
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="overrideDate">
                <FormControl sx={{ width: '100%' }} variant="outlined">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={['DatePicker']}>
                      <DatePicker
                        label="Override Date"
                        value={overrideDate}
                        onChange={(newValue) => setOverrideDate(newValue)}
                        sx={{ width: '100%' }}
                        format="DD/MM/YYYY"
                      />
                    </DemoContainer>
                  </LocalizationProvider>
                </FormControl>
              </Form.Group>
             
              <Form.Group as={Col} md={6} controlId="group_code">
                <FormControl sx={{ mt: 1, width: '100%' }} fullWidth>
                  <InputLabel id="group-code-label">Group Code</InputLabel>
                  <Controller
                    name="group_code"
                    control={control}
                    rules={{ required: 'Group Code is required' }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="group-code-label"
                        id="group-code-select"
                        label="Group Code"
                        value={field.value}
                      >
                        {groupCodes.map((item) => (
                          <MenuItem key={item._id} value={String(item._id)}>
                            {`${item.name} (${item.code})`}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.group_code && (
                    <p style={{ color: 'red', fontSize: '0.8rem' }}>{errors.group_code.message}</p>
                  )}
                </FormControl>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="lender_panel_id">
                <FormControl sx={{ width: '100%' }} fullWidth>
                  <InputLabel id="lender-panel-label">Lender Panel</InputLabel>
                  <Controller
                    name="lender_panel_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="lender-panel-label"
                        id="lender-panel-select"
                        label="Lender Panel"
                        value={field.value}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {lenderPanels.map((panel) => (
                          <MenuItem key={panel._id} value={String(panel._id)}>
                            {panel.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Form.Group>

              <Form.Group as={Col} md={6} controlId="partner_id">
                <FormControl sx={{ width: '100%' }} fullWidth>
                  <InputLabel id="partner-label">Partner</InputLabel>
                  <Controller
                    name="partner_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="partner-label"
                        id="partner-select"
                        label="Partner"
                        value={field.value}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {partners.map((partner) => (
                          <MenuItem key={partner._id} value={String(partner._id)}>
                            {partner.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="acquisition">
                <FormControl sx={{ width: '100%' }} fullWidth>
                  <InputLabel id="acquisition-label">Acquisition</InputLabel>
                  <Controller
                    name="acquisition"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="acquisition-label"
                        id="acquisition-select"
                        label="Acquisition"
                      >
                        <MenuItem value="Inbound">Inbound</MenuItem>
                        <MenuItem value="Outbound">Outbound</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Form.Group>
            </Row>

            <h6 className="mb-3 mt-4">Customer Address</h6>
            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="address1">
                <FormControl sx={{ width: '100%' }} variant="outlined">
                  <Controller
                    name="address1"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="address1"
                        label="Address"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                </FormControl>
              </Form.Group>
              <Form.Group as={Col} md={6} controlId="address2">
                <FormControl sx={{ width: '100%' }} variant="outlined">
                  <Controller
                    name="address2"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="address2"
                        label="Address2"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                </FormControl>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md={4} controlId="town">
                <FormControl sx={{ width: '100%' }} variant="outlined">
                  <Controller
                    name="town"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="town"
                        label="Town"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                </FormControl>
              </Form.Group>
              <Form.Group as={Col} md={4} controlId="country">
                <FormControl sx={{ width: '100%' }} variant="outlined">
                  <Controller
                    name="country"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="country"
                        label="Country"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />
                </FormControl>
              </Form.Group>
              <Form.Group as={Col} md={4} controlId="postcode">
                <FormControl sx={{ width: '100%' }} variant="outlined">
                  <Controller
                    name="postcode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="postcode"
                        label="PostCode"
                        variant="outlined"
                        fullWidth
                        onBlur={async (e) => {
                          field.onBlur();
                          const postcodeValue = e.target.value.trim();
                          if (postcodeValue) {
                            console.log('🔍 OnBlur - Validating postcode:', postcodeValue);
                            await validatePostcode(postcodeValue);
                          } else {
                            setIsPostcodeValid(false);
                            setPostcodeError('');
                            setPostcodeMatch(null);
                          }
                        }}
                        onChange={(e) => {
                          field.onChange(e);
                          if (postcodeError) {
                            setPostcodeError('');
                            setIsPostcodeValid(false);
                            setPostcodeMatch(null);
                          }
                        }}
                        error={!!postcodeError}
                        helperText={
                          checkingPostcode
                            ? 'Validating postcode...'
                            : postcodeError
                            ? postcodeError
                            : isPostcodeValid && postcodeMatch
                            ? `✓ Valid postcode - Branch: ${postcodeMatch.name}`
                            : ''
                        }
                        disabled={checkingPostcode}
                        sx={{
                          '& .MuiFormHelperText-root.Mui-error': {
                            color: '#d32f2f'
                          },
                          '& .MuiFormHelperText-root:not(.Mui-error)': {
                            color: isPostcodeValid ? '#2e7d32' : 'inherit'
                          }
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Form.Group>
            </Row>
            
            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="typeOfService">
                <FormControl sx={{ width: '100%' }} fullWidth>
                  <InputLabel id="type-of-service-label">Type of Service</InputLabel>
                  <Controller
                    name="typeOfService"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="type-of-service-label"
                        id="type-of-service-select"
                        label="Type of Service"
                        value={field.value}
                      >
                        <MenuItem value="Single">Single</MenuItem>
                        <MenuItem value="Mirror">Mirror</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Controller
                  name="about"
                  control={control}
                  render={({ field }) => {
                    console.log('🔍 Rendering LSS Will checkboxes with field.value:', field.value);
                    // Ensure field.value is always an array
                    const currentValue = Array.isArray(field.value) ? field.value : [];
                    return (
                      <FormGroup>
                        {aboutOptions.map((option) => {
                          const isChecked = currentValue.includes(option.label);
                          console.log(`🔍 Checkbox "${option.label}" checked:`, isChecked);
                          return (
                            <FormControlLabel
                              key={option.label}
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  onChange={(e) => {
                                    const newValue = e.target.checked
                                      ? [...currentValue, option.label]
                                      : currentValue.filter((val) => val !== option.label);
                                    console.log('📝 Checkbox changed, new value:', newValue);
                                    field.onChange(newValue);
                                  }}
                                  sx={{
                                    '&.Mui-checked': {
                                      color: '#764ba2',
                                    },
                                  }}
                                />
                              }
                              label={option.label}
                            />
                          );
                        })}
                      </FormGroup>
                    );
                  }}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Form.Group as={Col} md={6} controlId="approximateValueOfAssets">
                <FormControl sx={{ width: '100%' }} fullWidth>
                  <InputLabel id="approximate-value-label">Approximate Value of Assets</InputLabel>
                  <Controller
                    name="approximateValueOfAssets"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="approximate-value-label"
                        id="approximate-value-select"
                        label="Approximate Value of Assets"
                      >
                        <MenuItem value="Upto £500,000">Upto £500,000</MenuItem>
                        <MenuItem value="£500,000 - £1,000,000">£500,000 - £1,000,000</MenuItem>
                        <MenuItem value="Over £1,000,000">Over £1,000,000</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Form.Group>
            </Row>

            <Stack direction="row" spacing={2} className="mt-4">
              <Button 
                variant="outlined" 
                onClick={() => navigate('/leads')}
                className="red-outline-btn text-capitalize"
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                className="red-bg red-button text-capitalize" 
                type="submit" 
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
              </Button>
            </Stack>
          </Form>
        </div>
      </div>
    </>
  );
}